import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { stripeBillingUrl } from "@froppio/host-auth";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { subscriptionApiService } from "../services/subscriptionApiService";
import {
  fromSubscriptionDtoToSubscription,
  type SubscriptionSku,
} from "../types/subscriptionTypes";

export const subscriptionKeys = {
  all: ["subscription"] as const,
  detail: (businessId: string) => [...subscriptionKeys.all, businessId] as const,
  user: (businessId: string, userId: string) => [...subscriptionKeys.detail(businessId), userId] as const,
};

export const subscriptionOptions = (businessId: string, userId: string) => ({
  queryKey: subscriptionKeys.user(businessId, userId),
  queryFn: async ({ signal }: { signal: AbortSignal }) =>
    fromSubscriptionDtoToSubscription(await subscriptionApiService.getByBusinessId(businessId, signal)),
  staleTime: 0,
  retry: false,
});

export const useSubscription = (businessId: string | undefined, userId: string | undefined, enabled = true, staleTime = 0) => useQuery({
  ...subscriptionOptions(businessId ?? "", userId ?? ""),
  staleTime,
  enabled: enabled && !!businessId && !!userId,
});

/**
 * The current business plan for gating hints; only its owner can read it.
 * Briefly cached so opening conversations doesn't refetch it; rejected sends invalidate it.
 */
export const useCurrentSubscription = () => {
  const { user } = useAuth();
  const { business, businessId } = useCurrentBusiness();
  const owner = !!user && !!business && business.ownerId === user.id;
  return useSubscription(businessId, user?.id, owner, 60_000);
};

const invalidateOnFailure = (businessId: string, queryClient: ReturnType<typeof useQueryClient>) =>
  void queryClient.invalidateQueries({ queryKey: subscriptionKeys.detail(businessId) });

export const useSubscriptionCheckout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { action: "subscription.checkout" },
    retry: false,
    mutationFn: async ({ businessId, sku }: { businessId: string; sku: SubscriptionSku }) =>
      stripeBillingUrl((await subscriptionApiService.createCheckout(businessId, { sku })).url),
    onError: (_error, { businessId }) => invalidateOnFailure(businessId, queryClient),
  });
};

export const useSubscriptionPortal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { action: "subscription.portal" },
    retry: false,
    mutationFn: async ({ businessId }: { businessId: string }) =>
      stripeBillingUrl((await subscriptionApiService.createPortal(businessId)).url),
    onError: (_error, { businessId }) => invalidateOnFailure(businessId, queryClient),
  });
};
